import { observer } from "mobx-react-lite";
import { FunctionComponent } from "react";

import { DateTime } from "@bps/date-time";
import { useBookingCalendarScreenContext } from "@modules/booking/screens/booking-calendar/context/BookingCalendarScreenContext.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { SubmissionFormProps } from "@ui-components/form/submission-form/SubmissionForm.types.ts";

import { WorkingHoursFormFallback } from "../shared-components/WorkingHoursFormFallback.tsx";
import {
  AwayChoice,
  EditUserDayWorkingHoursData,
  EditUserDayWorkingHoursFormValues,
  EditUserDayWorkingHoursProps
} from "./components/EditUserDayWorkingHours.types.ts";
import {
  EditUserDayWorkingHoursContext,
  useEditUserDayWorkingHoursContext
} from "./context/EditUserDayWorkingHoursContext.ts";
import { EditUserDayWorkingHoursModel } from "./context/EditUserDayWorkingHoursModel.ts";
import { EditUserDayWorkingHoursFormFields } from "./EditUserDayWorkingHoursFormFields.tsx";
import { OutsidePracticeHoursWarning } from "./OutsidePracticeHoursWarning.tsx";
import { EditUserDayWorkingHoursFormValidator } from "./validators/EditUserDayWorkingHoursValidator.tsx";

const EditUserDayWorkingHoursDialogBase: FunctionComponent<EditUserDayWorkingHoursProps> =
  observer(({ user, onClose }) => {
    const { booking } = useStores();
    const { fetch, handleSubmitForm, getInitialValues } =
      useEditUserDayWorkingHoursContext();

    const reloadDismiss = async () => {
      await booking.getUserAvailability(user.id, {
        ignoreCache: true
      });

      onClose();
    };

    return (
      <DataFetcher<EditUserDayWorkingHoursData>
        fetch={fetch}
        noExceptionsHandlers
      >
        {(data, loading, error) => {
          const initialValues = data ? getInitialValues(data) : undefined;

          const getButtonProps: SubmissionFormProps<EditUserDayWorkingHoursFormValues>["buttonsProps"] =
            form => {
              const { values } = form.getState();

              if (!values.date || values.awayChoice === AwayChoice.away)
                return {};

              const start = DateTime.fromJSDateAndTime(
                values.date,
                values.startTime
              );

              const end = DateTime.fromJSDateAndTime(
                values.date,
                values.endTime
              );

              const practiceHours = data?.orgUnitHours?.timeRanges.find(
                timeRange => timeRange.from.hasSame(start, "day")
              );

              const promptOnSubmit =
                !practiceHours ||
                practiceHours.from > start ||
                practiceHours.to < end;

              return {
                promptOnSubmit,
                submitConfirmProps: {
                  title: "Provider working hours outside of practice hours",
                  dialogContentProps: {},
                  confirmButtonProps: {
                    text: "Change hours"
                  },
                  children: (
                    <OutsidePracticeHoursWarning
                      user={user}
                      conflicts={[
                        {
                          start,
                          end,
                          practiceHours
                        }
                      ]}
                    />
                  )
                }
              };
            };

          return (
            <SubmissionFormDialog<EditUserDayWorkingHoursFormValues>
              dialogName="Provider working hours dialog"
              initialValues={initialValues}
              onSubmit={handleSubmitForm}
              onSubmitSucceeded={reloadDismiss}
              validate={values => {
                const validator = new EditUserDayWorkingHoursFormValidator();
                return validator.validate(values);
              }}
              dialogProps={{
                onDismiss: onClose,
                minWidth: 600,
                dialogContentProps: { title: `${user.fullName} working hours` }
              }}
              loadingData={loading}
              dataLoadingError={error?.message}
              Fallback={WorkingHoursFormFallback}
              buttonsProps={getButtonProps}
            >
              {() => <EditUserDayWorkingHoursFormFields />}
            </SubmissionFormDialog>
          );
        }}
      </DataFetcher>
    );
  });

export const EditUserDayWorkingHoursDialog: FunctionComponent<
  EditUserDayWorkingHoursProps
> = props => {
  const { booking } = useStores();
  const { startDate } = useBookingCalendarScreenContext();

  const model = new EditUserDayWorkingHoursModel(
    booking,
    props.user,
    startDate
  );
  return (
    <EditUserDayWorkingHoursContext.Provider value={model}>
      <EditUserDayWorkingHoursDialogBase {...props} />
    </EditUserDayWorkingHoursContext.Provider>
  );
};
